"use client";

import type React from "react";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Box,
  Button,
  Flex,
  IconButton,
  Text,
  Textarea,
  VStack,
  Accordion,
  useToast,
  Spinner,
  Divider,
} from "@chakra-ui/react";
import { ArrowLeft, Save, Copy } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { RetellWebClient } from "retell-client-js-sdk";
import {
  agentService,
  type Agent,
  type RetellLLM,
  type VoiceDetails,
} from "../../services/agentService.ts";
import { ModelSelect } from "./ModelSelect.tsx";
import { VoiceSelect } from "./VoiceSelect.tsx";
import { LanguageSelect } from "./LanguageSelect.tsx";
import { WelcomeMessageSelect } from "./WelcomeMessageSelect.tsx";
import { useAppTheme } from "../../ThemeContext.tsx";
import { callService } from "../../services/callService.ts";
import { WebhookSettings } from "./WebhookSettings.tsx";
import { SecuritySettings } from "./SecuritySettings.tsx";
import { CallSettings } from "./call-settings/CallSettings.tsx";
import { PostCallAnalysis } from "./post-call-analysis/PostCallAnalysis.tsx";
import { SpeechSettings } from "./speech-settings/SpeechSettings.tsx";
import { AgentKnowledgeBase } from "./knowledge-base/AgentKnowledgeBase.tsx";
import { AgentFunctionsWrapper } from "./functions/AgentFunctionsWrapper.tsx";
import { DynamicVariablesButton } from "./DynamicVariablesButton.tsx";
import { EditableAgentName } from "./EditableAgentName.tsx";
import PromptTreeEditor from "./PromptTreeEditor.tsx";

interface MultiPromptAgentConfigProps {
  agent: Agent;
}

function getShortId(id) {
  if (!id) return "";
  const len = id.length;
  return len > 5 ? `${id.slice(0, 2)}...${id.slice(-3)}` : id;
}

export const MultiPromptAgentConfig: React.FC<MultiPromptAgentConfigProps> = ({
  agent: initialAgent,
}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const retellWebClientRef = useRef<RetellWebClient | null>(null);

  const [agent, setAgent] = useState<Agent>(initialAgent);
  const [prompt, setPrompt] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [noteVisible, setNoteVisible] = useState(false);
  const [voices, setVoices] = useState<VoiceDetails[]>([]);
  const [selectedLLM, setSelectedLLM] = useState<RetellLLM | null>(null);
  const [selectedVoice, setSelectedVoice] = useState<VoiceDetails | null>(null);
  const [isCallStarting, setIsCallStarting] = useState(false);
  const [callData, setCallData] = useState<{
    accessToken: string;
    callId: string;
  } | null>(null);

  const [isCallActive, setIsCallActive] = useState(false);
  const [dynamicVariables, setDynamicVariables] = useState<
    Record<string, string>
  >({});

  // New state for prompt tree editor
  const [showPromptTreeEditor, setShowPromptTreeEditor] = useState(false);

  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  // Define scrollbar styles to reuse across components
  const scrollbarStyles = {
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "transparent",
      borderRadius: "6px",
      border: `2px solid transparent"}`,
    },
  };

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const voicesResponse = await agentService.listVoices();
        setVoices(voicesResponse);
        const currentLLM = await agentService.getRetellLLM(
          agent.response_engine.llm_id
        );
        setSelectedLLM(currentLLM);
        setPrompt(currentLLM.general_prompt || "");
        const foundVoice = voicesResponse.find(
          (v) => v.voice_id === agent.voice_id
        );
        if (foundVoice) {
          setSelectedVoice(foundVoice);
        }
      } catch (error) {
        console.error("Error loading data:", error);
        toast({
          title: "Error loading data",
          status: "error",
          duration: 3000,
        });
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [agent, toast]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied to clipboard",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  const handleSave = async () => {
    if (!selectedLLM) return;
    setIsSaving(true);
    try {
      const updated = await agentService.updateRetellLLM(
        selectedLLM.llm_id,
        {
          agent_id: agent.agent_id,
          general_prompt: prompt,
        }
      );
      setSelectedLLM(updated);
      toast({
        title: "Prompt saved successfully",
        status: "success",
        duration: 3000,
      });
    } catch (err: any) {
      console.error(err);
      toast({
        title: "Error saving prompt",
        description: err.message,
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const memoizedWelcomeMessageSelect = useMemo(() => {
    if (!selectedLLM) return null;
    return (
      <WelcomeMessageSelect
        initialLLM={selectedLLM}
        agentId={agent.agent_id}
        onLLMUpdate={(updatedLLM: RetellLLM) => setSelectedLLM(updatedLLM)}
      />
    );
  }, [selectedLLM, agent.agent_id]);

  // Add this new component for the prompt tree visualization
  const promptTreeComponent = useMemo(() => {
    return (
      <Box
        mt={4}
        border="1px solid"
        borderColor={isDark ? "gray.700" : "gray.200"}
        borderRadius="lg"
        position="relative"
        h="200px"
        w="full"
        display="flex"
        alignItems="center"
        justifyContent="center"
        cursor="pointer"
        bg={isDark ? "gray.800" : "white"}
      >
        <img
          src={`${process.env.PUBLIC_URL}/tree_preview.webp`}
          alt="Prompt Tree"
          style={{
            height: "200px",
            objectFit: "contain",
            opacity: 0.8,
          }}
        />

        <Button
          position="absolute"
          inset="0"
          m="auto"
          h="fit-content"
          w="fit-content"
          bg={isDark ? "gray.700" : "white"}
          color={isDark ? "white" : "gray.800"}
          border="1px solid"
          borderColor={isDark ? "gray.600" : "gray.200"}
          _hover={{ bg: isDark ? "gray.600" : "gray.100" }}
          onClick={() => setShowPromptTreeEditor(true)}
        >
          Edit Prompt Tree
        </Button>
      </Box>
    );
  }, [isDark]);

  const initializeRetellWebClient = useCallback(() => {
    if (!retellWebClientRef.current) {
      retellWebClientRef.current = new RetellWebClient();
      retellWebClientRef.current.on("call_started", () => {
        console.log("call started");
        setIsCallActive(true);
      });
      retellWebClientRef.current.on("call_ended", () => {
        console.log("call ended");
        setIsCallActive(false);
        setCallData(null);
      });
      retellWebClientRef.current.on("agent_start_talking", () => {
        console.log("agent_start_talking");
      });
      retellWebClientRef.current.on("agent_stop_talking", () => {
        console.log("agent_stop_talking");
      });
      retellWebClientRef.current.on("error", (error) => {
        console.error("An error occurred:", error);
        toast({
          title: "Call Error",
          description:
            error.response?.data?.message ||
            "An error occurred during the call",
          status: "error",
          duration: 5000,
        });
        retellWebClientRef.current?.stopCall();
      });
    }
  }, [toast]);

  const joinCall = useCallback(
    async (accessToken: string) => {
      if (!retellWebClientRef.current) {
        initializeRetellWebClient();
      }
      try {
        await retellWebClientRef.current?.startCall({
          accessToken,
          sampleRate: 24000,
        });
        toast({
          title: "Joined call successfully",
          status: "success",
          duration: 3000,
        });
      } catch (error) {
        console.error("Error joining call:", error);
        toast({
          title: "Error joining call",
          description:
            error instanceof Error
              ? error.message
              : "An unknown error occurred",
          status: "error",
          duration: 5000,
        });
      }
    },
    [initializeRetellWebClient, toast]
  );

  const handleStartCall = useCallback(async () => {
    setIsCallStarting(true);

    try {
      // Only include dynamic variables if there are any
      const callOptions: any = {};

      if (Object.keys(dynamicVariables).length > 0) {
        callOptions.retell_llm_dynamic_variables = dynamicVariables;
      }

      const response = await callService.createWebCall(
        agent.agent_id,
        callOptions
      );
      const newCallData = {
        accessToken: response.access_token,
        callId: response.call_id,
      };
      setCallData(newCallData);
      toast({
        title: "Web call created",
        description: `Call ID: ${response.call_id}`,
        status: "success",
        duration: 5000,
      });
      await joinCall(response.access_token);
    } catch (error) {
      console.error("Error starting web call:", error);
      toast({
        title: "Error starting web call",
        description:
          error instanceof Error ? error.message : "An unknown error occurred",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsCallStarting(false);
    }
  }, [agent.agent_id, toast, joinCall, dynamicVariables]);

  const handleEndCall = useCallback(() => {
    retellWebClientRef.current?.stopCall();
    setIsCallActive(false);
    setCallData(null);
  }, []);

  // Handle dynamic variables change with less logging
  const handleDynamicVariablesChange = useCallback(
    (vars: Record<string, string>) => {
      // Only log when there are actual variables to reduce console spam
      if (Object.keys(vars).length > 0) {
        console.log("Variables updated:", vars);
      }
      setDynamicVariables(vars);
    },
    []
  );

  const handleAgentSyncedWithNewLlm = useCallback(
    (llmId: string, opts?: { coercedVoiceId?: string }) => {
      setAgent((prev) => ({
        ...prev,
        response_engine: { ...prev.response_engine, llm_id: llmId },
        ...(opts?.coercedVoiceId
          ? { voice_id: opts.coercedVoiceId, voice_model: null }
          : {}),
      }));
    },
    []
  );

  const testPanel = useMemo(
    () => (
      <VStack
        w="250px"
        p={4}
        borderLeft="1px solid"
        borderColor={isDark ? "gray.700" : "gray.200"}
        spacing={4}
      >
        <Flex
          align="center"
          justify="space-between"
          w="full"
          bg={isDark ? "gray.800" : "gray.200"}
        >
          <Text px={3} py={1} rounded="md" fontWeight="semibold">
            Test your agent
          </Text>
          <DynamicVariablesButton
            onVariablesChange={handleDynamicVariablesChange}
            prompt={prompt}
            beginMessage={selectedLLM?.begin_message || ""}
          />
        </Flex>

        <Divider />

        <Box textAlign="center" py={8}>
          <Box as="span" fontSize="48px">
            🎙️
          </Box>
          <Button
            mt={4}
            w="full"
            onClick={isCallActive ? handleEndCall : handleStartCall}
            isLoading={isCallStarting}
            loadingText="Starting call..."
            colorScheme="gray"
            bg="transparent"
            textColor={isDark ? "whiteAlpha.900" : "gray.800"}
            _hover={{ bg: isDark ? "gray.800" : "gray.100" }}
          >
            {isCallActive ? "End Call" : "Start Call"}
          </Button>
        </Box>
      </VStack>
    ),
    [
      isDark,
      isCallActive,
      handleEndCall,
      handleStartCall,
      isCallStarting,
      handleDynamicVariablesChange,
      prompt,
      selectedLLM?.begin_message,
    ]
  );

  const mainContent = useMemo(
    () => (
      <Flex
        h="100vh"
        bg={isDark ? "gray.900" : "gray.100"}
        color={isDark ? "white" : "gray.800"}
        direction="column"
      >
        <Flex
          px={4}
          py={3}
          borderBottom="1px solid"
          borderColor={isDark ? "gray.700" : "gray.200"}
          align="center"
        >
          <IconButton
            aria-label="Back"
            icon={<ArrowLeft />}
            variant="ghost"
            color={isDark ? "white" : "gray.800"}
            _hover={{ bg: isDark ? "gray.700" : "gray.200" }}
            onClick={() => navigate("/agents")}
          />
          <Box ml={4}>
            <EditableAgentName
              agentId={agent.agent_id}
              initialName={agent.agent_name}
            />
            <Text color="gray.500" fontSize="sm">
              Single prompt &bull; Agent ID: {getShortId(agent.agent_id)}{" "}
              <IconButton
                aria-label="Copy Agent ID"
                icon={<Copy size={12} />}
                size="xs"
                variant="ghost"
                onClick={() => copyToClipboard(agent.agent_id)}
                color="gray.500"
              />
              &nbsp;&bull;&nbsp; LLM ID:{" "}
              {getShortId(agent.response_engine.llm_id)}{" "}
              <IconButton
                aria-label="Copy LLM ID"
                icon={<Copy size={12} />}
                size="xs"
                variant="ghost"
                onClick={() => copyToClipboard(agent.response_engine.llm_id)}
                color="gray.500"
              />
            </Text>
          </Box>
        </Flex>

        <Flex
          px={4}
          py={2}
          borderBottom="1px solid"
          borderColor={isDark ? "gray.700" : "gray.200"}
          gap={2}
        >
          <ModelSelect
            initialLLM={selectedLLM}
            agentId={agent.agent_id}
            setSelectedLLM={setSelectedLLM}
            onAgentSyncedWithNewLlm={handleAgentSyncedWithNewLlm}
          />
          <VoiceSelect
            initialVoice={selectedVoice}
            voices={voices}
            agentId={agent.agent_id}
            setSelectedVoice={setSelectedVoice}
          />
          <LanguageSelect
            initialLanguage={agent.language}
            agentId={agent.agent_id}
          />
        </Flex>

        <Flex flex={1} overflow="hidden" alignItems="stretch">
          <Box
            flex={1}
            p={4}
            borderRight="1px solid"
            borderColor={isDark ? "gray.700" : "gray.200"}
          >
            {/* This is the container with a single scrollbar for all three components */}
            <VStack
              align="stretch"
              h="full"
              spacing={4}
              overflowY="auto"
              css={scrollbarStyles}
            >
              <Box position="relative" flex={0} minH="400px">
                <Textarea
                  sx={
                    isDark
                      ? {
                          "&::-webkit-scrollbar": {
                            height: "8px",
                          },
                          "&::-webkit-scrollbar-track": {
                            backgroundColor: "#2D3748", // Dark track color
                          },
                          "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "#4A5568", // Dark thumb color
                            borderRadius: "2px",
                          },
                          // For Firefox
                          scrollbarColor: "#4A5568 #2D3748", // thumb, then track
                          _hover: {
                            scrollbarColor: "#4A5568 #2D3748", // same colors on hover
                          },
                        }
                      : {
                          "&::-webkit-scrollbar": {
                            height: "4px",
                          },
                          "&::-webkit-scrollbar-button": {
                            display: "none",
                          },
                          "&::-webkit-scrollbar-track": {
                            backgroundColor: "#fff",
                          },
                          "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "#fff",
                            borderRadius: "2px",
                          },
                          scrollbarColor: "rgb(220, 220, 220) white",
                          _hover: {
                            scrollbarColor: "rgb(220, 220, 220) white",
                          },
                        }
                  }
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  bg={isDark ? "gray.800" : "white"}
                  border="none"
                  _focus={{ border: "none", boxShadow: "none" }}
                  resize="none"
                  fontSize="sm"
                  placeholder="Type in a universal prompt for your agent, such as its role, conversational style, objective, etc."
                  pr="100px"
                  h="100%"
                  onMouseEnter={() => setNoteVisible(true)}
                  onMouseLeave={() => setNoteVisible(false)}
                  zIndex={1}
                />
                <Button
                  leftIcon={<Save size={16} />}
                  onClick={handleSave}
                  isLoading={isSaving}
                  position="absolute"
                  bottom="8px"
                  right="8px"
                  size="sm"
                  colorScheme={isDark ? "black" : "gray"}
                  textColor={isDark ? "white" : "gray.800"}
                  bg={isDark ? "gray.700" : "gray.100"}
                  _hover={{
                    textColor: isDark ? "gray.300" : "gray.500",
                  }}
                  zIndex={2}
                >
                  Save
                </Button>
                {noteVisible && (
                  <Text
                    position="absolute"
                    bottom="8px"
                    left="8px"
                    fontSize="xs"
                    color="gray.500"
                  >
                    Use `{"{{_}}"}` to add variables.
                  </Text>
                )}
              </Box>
              {memoizedWelcomeMessageSelect}
              {promptTreeComponent}
            </VStack>
          </Box>

          <VStack
            w="300px"
            spacing={0}
            overflow="auto"
            p={4}
            borderRight="1px solid"
            borderColor={isDark ? "gray.700" : "gray.200"}
            sx={scrollbarStyles}
          >
            <Accordion allowMultiple w="full">
              <AgentFunctionsWrapper
                agentId={agent.agent_id}
                llmId={agent.response_engine.llm_id}
                initialFunctions={agent.response_engine.general_tools || []}
              />
              <AgentKnowledgeBase
                agentId={agent.agent_id}
                llmId={agent.response_engine.llm_id}
                initialLLM={selectedLLM}
                onLLMUpdate={setSelectedLLM}
              />
              <SpeechSettings agentId={agent.agent_id} />
              <CallSettings
                agentId={agent.agent_id}
                initialSettings={{
                  enable_voicemail_detection: agent.enable_voicemail_detection,
                  end_call_after_silence_ms: agent.end_call_after_silence_ms,
                  max_call_duration_ms: agent.max_call_duration_ms,
                  begin_message_delay_ms: agent.begin_message_delay_ms,
                }}
              />
              <PostCallAnalysis
                agentId={agent.agent_id}
                initialAnalysisData={agent.post_call_analysis_data}
              />
              <SecuritySettings
                agentId={agent.agent_id}
                initialOptOut={agent.opt_out_sensitive_data_storage || false}
                initialFallbackVoiceIds={agent.fallback_voice_ids || null}
                agent={agent}
              />
              <WebhookSettings
                agentId={agent.agent_id}
                initialWebhookUrl={agent.webhook_url}
              />
            </Accordion>
          </VStack>

          {testPanel}
        </Flex>
      </Flex>
    ),
    [
      agent,
      prompt,
      voices,
      selectedLLM,
      selectedVoice,
      isDark,
      noteVisible,
      memoizedWelcomeMessageSelect,
      navigate,
      testPanel,
      isSaving,
      promptTreeComponent,
      handleAgentSyncedWithNewLlm,
    ]
  );

  return isLoading ? (
    <Flex
      h="100vh"
      justify="center"
      align="center"
      bg={isDark ? "gray.900" : "gray.100"}
    >
      <Spinner size="xl" />
    </Flex>
  ) : (
    <Flex
      h="100vh"
      bg={isDark ? "gray.900" : "gray.100"}
      color={isDark ? "white" : "gray.800"}
      direction="column"
    >
      {!showPromptTreeEditor && (
        <>
          <Flex
            px={4}
            py={3}
            borderBottom="1px solid"
            borderColor={isDark ? "gray.700" : "gray.200"}
            align="center"
          >
            <IconButton
              aria-label="Back"
              icon={<ArrowLeft />}
              variant="ghost"
              color={isDark ? "white" : "gray.800"}
              _hover={{ bg: isDark ? "gray.700" : "gray.200" }}
              onClick={() => navigate("/agents")}
            />
            <Box ml={4}>
              <EditableAgentName
                agentId={agent.agent_id}
                initialName={agent.agent_name}
              />
              <Text color="gray.500" fontSize="sm">
                Single prompt &bull; Agent ID: {getShortId(agent.agent_id)}{" "}
                <IconButton
                  aria-label="Copy Agent ID"
                  icon={<Copy size={12} />}
                  size="xs"
                  variant="ghost"
                  onClick={() => copyToClipboard(agent.agent_id)}
                  color="gray.500"
                />
                &nbsp;&bull;&nbsp;LLM ID:{" "}
                {getShortId(agent.response_engine.llm_id)}{" "}
                <IconButton
                  aria-label="Copy LLM ID"
                  icon={<Copy size={12} />}
                  size="xs"
                  variant="ghost"
                  onClick={() => copyToClipboard(agent.response_engine.llm_id)}
                  color="gray.500"
                />
              </Text>
            </Box>
          </Flex>

          <Flex
            px={4}
            py={2}
            borderBottom="1px solid"
            borderColor={isDark ? "gray.700" : "gray.200"}
            gap={2}
          >
            <ModelSelect
              initialLLM={selectedLLM}
              agentId={agent.agent_id}
              setSelectedLLM={setSelectedLLM}
              onAgentSyncedWithNewLlm={handleAgentSyncedWithNewLlm}
            />
            <VoiceSelect
              initialVoice={selectedVoice}
              voices={voices}
              agentId={agent.agent_id}
              setSelectedVoice={setSelectedVoice}
            />
            <LanguageSelect
              initialLanguage={agent.language}
              agentId={agent.agent_id}
            />
          </Flex>
        </>
      )}

      <Flex flex={1} overflow="hidden" alignItems="stretch">
        {showPromptTreeEditor ? (
          // Then in the JSX where PromptTreeEditor is used, update the component props:
          <PromptTreeEditor
            onBack={() => setShowPromptTreeEditor(false)}
            agentId={agent.agent_id}
          />
        ) : (
          <>
            <Box
              flex={1}
              p={4}
              borderRight="1px solid"
              borderColor={isDark ? "gray.700" : "gray.200"}
            >
              {/* This is the container with a single scrollbar for all three components */}
              <VStack
                align="stretch"
                h="full"
                spacing={4}
                overflowY="auto"
                css={scrollbarStyles}
              >
                <Box position="relative" flex={0} minH="400px">
                  <Textarea
                    sx={
                      isDark
                        ? {
                            "&::-webkit-scrollbar": {
                              height: "8px",
                            },
                            "&::-webkit-scrollbar-track": {
                              backgroundColor: "#2D3748", // Dark track color
                            },
                            "&::-webkit-scrollbar-thumb": {
                              backgroundColor: "#4A5568", // Dark thumb color
                              borderRadius: "2px",
                            },
                            // For Firefox
                            scrollbarColor: "#4A5568 #2D3748", // thumb, then track
                            _hover: {
                              scrollbarColor: "#4A5568 #2D3748", // same colors on hover
                            },
                          }
                        : {
                            "&::-webkit-scrollbar": {
                              height: "4px",
                            },
                            "&::-webkit-scrollbar-button": {
                              display: "none",
                            },
                            "&::-webkit-scrollbar-track": {
                              backgroundColor: "#fff",
                            },
                            "&::-webkit-scrollbar-thumb": {
                              backgroundColor: "#fff",
                              borderRadius: "2px",
                            },
                            scrollbarColor: "rgb(220, 220, 220) white",
                            _hover: {
                              scrollbarColor: "rgb(220, 220, 220) white",
                            },
                          }
                    }
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    bg={isDark ? "gray.800" : "white"}
                    border="none"
                    _focus={{ border: "none", boxShadow: "none" }}
                    resize="none"
                    fontSize="sm"
                    placeholder="Type in a universal prompt for your agent, such as its role, conversational style, objective, etc."
                    pr="100px"
                    h="100%"
                    onMouseEnter={() => setNoteVisible(true)}
                    onMouseLeave={() => setNoteVisible(false)}
                    zIndex={1}
                  />
                  <Button
                    leftIcon={<Save size={16} />}
                    onClick={handleSave}
                    isLoading={isSaving}
                    position="absolute"
                    bottom="8px"
                    right="8px"
                    size="sm"
                    colorScheme={isDark ? "black" : "gray"}
                    textColor={isDark ? "white" : "gray.800"}
                    bg={isDark ? "gray.700" : "gray.100"}
                    _hover={{
                      textColor: isDark ? "gray.300" : "gray.500",
                    }}
                    zIndex={2}
                  >
                    Save
                  </Button>
                  {noteVisible && (
                    <Text
                      position="absolute"
                      bottom="8px"
                      left="8px"
                      fontSize="xs"
                      color="gray.500"
                    >
                      Use `{"{{_}}"}` to add variables.
                    </Text>
                  )}
                </Box>
                {memoizedWelcomeMessageSelect}
                {promptTreeComponent}
              </VStack>
            </Box>

            <VStack
              w="300px"
              spacing={0}
              overflow="auto"
              p={4}
              borderRight="1px solid"
              borderColor={isDark ? "gray.700" : "gray.200"}
              sx={scrollbarStyles}
            >
              <Accordion allowMultiple w="full">
                <AgentFunctionsWrapper
                  agentId={agent.agent_id}
                  llmId={agent.response_engine.llm_id}
                  initialFunctions={agent.response_engine.general_tools || []}
                />
                <AgentKnowledgeBase
                  agentId={agent.agent_id}
                  llmId={agent.response_engine.llm_id}
                  initialLLM={selectedLLM}
                  onLLMUpdate={setSelectedLLM}
                />
                <SpeechSettings agentId={agent.agent_id} />
                <CallSettings
                  agentId={agent.agent_id}
                  initialSettings={{
                    enable_voicemail_detection:
                      agent.enable_voicemail_detection,
                    end_call_after_silence_ms: agent.end_call_after_silence_ms,
                    max_call_duration_ms: agent.max_call_duration_ms,
                    begin_message_delay_ms: agent.begin_message_delay_ms,
                  }}
                />
                <PostCallAnalysis
                  agentId={agent.agent_id}
                  initialAnalysisData={agent.post_call_analysis_data}
                />
                <SecuritySettings
                  agentId={agent.agent_id}
                  initialOptOut={agent.opt_out_sensitive_data_storage || false}
                  initialFallbackVoiceIds={agent.fallback_voice_ids || null}
                  agent={agent}
                />
                <WebhookSettings
                  agentId={agent.agent_id}
                  initialWebhookUrl={agent.webhook_url}
                />
              </Accordion>
            </VStack>
          </>
        )}

        {testPanel}
      </Flex>
    </Flex>
  );
};
